import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowDown } from "lucide-react";
import { MaskedLines, Reveal } from "../components/Motion";
import { ProjectCard } from "../components/ProjectCard";
import { PartnerMarquee } from "../components/PartnerMarquee";
import { PROJECTS, IMAGES, STATS, SITE } from "../lib/data";
import { AssociationBand } from "../components/AssociationBand";
import { SocialFeed } from "../components/SocialFeed";
import { LinkedInInsights } from "../components/LinkedInInsights";

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative h-[100svh] overflow-hidden grain">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img src={IMAGES.hero} alt="Luxury residence exterior" className="w-full h-full object-cover" />
      </motion.div>
      {/* Base tint for overall legibility */}
      <div className="absolute inset-0 bg-[#1A1A1A]/35" />
      {/* Strong bottom gradient so the headline + CTA sit on dark ground */}
      <div className="absolute inset-0 bg-gradient-to-t from-[#0D0D0D] via-[#1A1A1A]/70 to-transparent" />

      <div className="relative z-10 h-full max-w-[1400px] mx-auto px-6 md:px-10 flex flex-col justify-end pb-20 md:pb-28">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 1 }}
          className="overline text-[#E5DFD3] mb-6"
        >
          Noida Expressway · Uttar Pradesh · Goa
        </motion.p>
        <h1 className="font-serif-lux text-[#FDFBF7] text-[13vw] md:text-[7.5vw] leading-[0.92] tracking-tight drop-shadow-[0_2px_18px_rgba(0,0,0,0.55)]">
          <MaskedLines lines={["Real estate,", "marketed with", "conviction."]} delay={0.3} />
        </h1>
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mt-10">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.9 }}
            className="font-sans-lux text-[#FDFBF7] max-w-md text-base leading-relaxed drop-shadow-[0_1px_8px_rgba(0,0,0,0.6)]"
          >
            Advisory and go-to-market for residential, plotted and farmhouse developments - led by a {SITE.teamSize}-strong sales team across the NCR corridor and North Goa.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.25, duration: 0.9 }}
          >
            <Link
              to="/contact"
              data-testid="hero-cta-button"
              className="inline-flex items-center gap-3 bg-[#FDFBF7] text-[#1A1A1A] btn-lux btn-lux-invert px-9 py-5 overline"
            >
              Book a Site Visit <ArrowUpRight size={16} />
            </Link>
          </motion.div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-8 right-6 md:right-10 z-10 text-[#FDFBF7]"
      >
        <ArrowDown className="animate-bounce" size={22} />
      </motion.div>
    </section>
  );
};

export default function Home() {
  const featured = PROJECTS.slice(0, 4);
  return (
    <div data-testid="home-page">
      <Hero />
      <PartnerMarquee />

      {/* Intro / positioning */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-28 md:py-40">
        <div className="grid md:grid-cols-12 gap-12">
          <div className="md:col-span-4">
            <Reveal>
              <p className="overline text-[#C19A6B]">Who I am</p>
            </Reveal>
          </div>
          <div className="md:col-span-8">
            <Reveal delay={0.1}>
              <p className="font-serif-lux text-3xl md:text-5xl leading-[1.15] text-[#1A1A1A] tracking-tight">
                I bring developers' projects to market - from listing and brochure strategy through to the tools my sales team uses every day to close deals across the Noida Expressway corridor and Goa.
              </p>
            </Reveal>
            <Reveal delay={0.2}>
              <Link to="/about" className="inline-flex items-center gap-2 mt-10 overline text-[#1A1A1A] link-sweep">
                Read my story <ArrowUpRight size={15} />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Featured projects */}
      <section data-testid="featured-projects" className="max-w-[1400px] mx-auto px-6 md:px-10 pb-28 md:pb-40">
        <Reveal>
          <div className="flex items-end justify-between mb-14 border-t border-[#E5DFD3] pt-8">
            <div>
              <p className="overline text-[#C19A6B]">Selected work</p>
              <h2 className="font-serif-lux text-5xl md:text-7xl text-[#1A1A1A] mt-3 tracking-tight">Featured Projects</h2>
            </div>
            <Link to="/projects" className="hidden md:inline-flex items-center gap-2 overline text-[#1A1A1A] link-sweep">
              All projects <ArrowUpRight size={15} />
            </Link>
          </div>
        </Reveal>

        <div className="grid md:grid-cols-2 gap-x-8 gap-y-16">
          {featured.map((p, i) => (
            <Reveal key={p.slug} delay={(i % 2) * 0.1}>
              <ProjectCard project={p} index={i} />
            </Reveal>
          ))}
        </div>
      </section>

      {/* Stats */}
      <section className="bg-[#F5F2EB] border-y border-[#E5DFD3]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 grid grid-cols-2 md:grid-cols-4 gap-y-12">
          {STATS.map((s, i) => (
            <Reveal key={s.label} delay={i * 0.08}>
              <div className="text-center md:text-left">
                <p className="font-serif-lux text-6xl md:text-7xl text-[#1A1A1A] tracking-tight">{s.value}</p>
                <p className="font-sans-lux text-sm text-[#5C5C5C] mt-2">{s.label}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <AssociationBand />

      <SocialFeed />

      <LinkedInInsights />

      {/* CTA */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-28 md:py-40 text-center">
        <Reveal>
          <p className="overline text-[#C19A6B]">Your next address</p>
          <h2 className="font-serif-lux text-5xl md:text-8xl text-[#1A1A1A] mt-6 leading-[0.95] tracking-tight">
            Let's book your<br />site visit.
          </h2>
          <Link
            to="/contact"
            data-testid="home-cta-button"
            className="inline-flex items-center gap-3 mt-12 border border-[#1A1A1A] text-[#1A1A1A] hover:text-[#FDFBF7] btn-lux px-10 py-5 overline"
          >
            Get in touch <ArrowUpRight size={16} />
          </Link>
        </Reveal>
      </section>
    </div>
  );
}
